import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subscription, forkJoin, of, throwError, timer } from 'rxjs';
import { catchError, defaultIfEmpty, exhaustMap, finalize, map, switchMap, timeout } from 'rxjs/operators';
import { BaseService } from 'src/modules/base/shared/services/base.service';
import { AlertsService } from 'src/modules/base/services/alerts.service';
import { SERVER_SEARCH_SELECT, SINGLE_SELECT, listQuestion, optionsOf } from './searchselect';
import { AgentTriggerEditorComponent, TriggerRow } from './agent-trigger-editor.component';

const APPLICATION = 'aiml-gateway';
const GUIDED_RESOURCE = 'ai_guided_setup';
const DOMAIN_RESOURCE = 'ai_applications';
const TOOL_RESOURCE = 'ai_tool_registry';
const MODEL_RESOURCE = 'ai_model_master';
const KNOWLEDGE_RESOURCE = 'ai_knowledge_base';
const TRIGGER_RESOURCE = 'ai_agent_trigger';

type StepKey = 'identity' | 'instructions' | 'model' | 'tools' | 'knowledge' | 'triggers' | 'guardrails' | 'memory' | 'test' | 'publish';

interface StepDef {
    key: StepKey;
    title: string;
    /** One line under the step's title, saying what it is for. */
    hint: string;
    /** Next does not require anything on it. */
    optional?: boolean;
}

/** The steps in order. The template and checks go by key, so a step can be added without renumbering. */
const STEPS: StepDef[] = [
    { key: 'identity', title: 'Identity', hint: 'Choose the domain the agent belongs to and name it.' },
    { key: 'instructions', title: 'Instructions', hint: 'Describe what the agent should do and how it should work.' },
    { key: 'model', title: 'Model', hint: 'Pick the provider and model that power the agent.' },
    { key: 'tools', title: 'Tools', hint: 'Let the agent call HTTP APIs when it needs live data.', optional: true },
    { key: 'knowledge', title: 'Knowledge', hint: 'Give the agent knowledge bases to search, and cite in its answers.', optional: true },
    { key: 'triggers', title: 'Triggers', hint: 'Run the agent on its own when an application event happens.', optional: true },
    { key: 'guardrails', title: 'Guardrails', hint: 'Screen every message before the agent sees it.', optional: true },
    { key: 'memory', title: 'Memory', hint: 'Keep earlier turns of a conversation as context.', optional: true },
    { key: 'test', title: 'Test', hint: 'Try the agent with a message before publishing it.', optional: true },
    { key: 'publish', title: 'Publish', hint: 'Review the setup, then publish it as a version.' },
];

const LIFECYCLES = ['stateless', 'session-scoped', 'tenant-scoped', 'long-lived'];

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/** The engine's one embedding model, as the knowledge base screen defaults it (assets/json/ai_knowledge_base.json). */
const KB_EMBEDDING = 'sentence-transformers/all-MiniLM-L6-v2';
const KB_EMBEDDING_DIMENSIONS = 384;
/** How often a building knowledge base's row is read again: the engine writes its progress there. */
const KB_POLL_MS = 3000;
const KB_READ_TIMEOUT_MS = 15000;
const KB_BUILDING = ['pending', 'processing'];
/** Uploaded files go to DMS as the knowledge base screen's file uploader sends them (ai_knowledge_base.json). */
const KB_DOCUMENT_TYPE = 'crm_documents';
const KB_FILE_FORMATS = '.pdf,.docx,.xlsx,.csv,.txt,.md,.html,.htm';
const KB_MAX_FILES = 20;
const KB_MAX_FILE_BYTES = 15 * 1024 * 1024;
const KB_UPLOAD_TIMEOUT_MS = 120000;
const KB_MAX_TEXT_CHARS = 100000;

type KnowledgeSource = 'website' | 'mongodb' | 'text' | 'file';

const KB_SOURCE_LABELS: Record<string, string> = { website: 'Website', mongodb: 'MongoDB', text: 'Text', file: 'Files' };

interface DomainOption {
    id: string;
    displayName: string;
}

/**
 * One ai_model_master row (the platform's model list, as AI Prompt Master uses it):
 * llm_name is the provider, model_name what the provider is called with.
 */
interface ModelOption {
    id: string;
    llmName: string;
    name: string;
}

interface ToolRow {
    name: string;
    slug: string;
    description: string;
    url: string;
    method: string;
}

/** A knowledge base the agent searches: only its id is saved; the rest is for display. */
interface KnowledgeRow {
    id: string;
    name: string;
    description: string;
    /** pending, processing, completed or failed (the engine's), '' until read, or 'not found'. */
    status: string;
    sourceType: string;
    /** While it builds, this build's counts so far. */
    documentCount: number;
    chunkCount: number;
    maxPages: number | null;
    startedAt: number | null;
    completedAt: number | null;
    /** Why a build failed, or a warning on a completed one. */
    errorMessage: string;
}

/** A knowledge base created from the Knowledge step, as the knowledge base screen would save it. */
interface KnowledgeForm {
    name: string;
    description: string;
    websiteUrls: string;
    maxDepth: number;
    maxPages: number;
    refreshHours: number;
    includePatterns: string;
    excludePatterns: string;
    connectionString: string;
    dbName: string;
    collectionName: string;
    fields: string;
    titleField: string;
    filter: string;
    textContent: string;
    /** DMS documents, as the file uploader stores them. */
    files: Array<{ documentId: string; documentName: string }>;
}

/** The knowledge base screen's defaults. */
function blankKnowledgeForm(): KnowledgeForm {
    return {
        name: '', description: '', websiteUrls: '', maxDepth: 2, maxPages: 200, refreshHours: 0,
        includePatterns: '', excludePatterns: '', connectionString: '', dbName: '', collectionName: '',
        fields: '', titleField: '', filter: '', textContent: '', files: [],
    };
}

function knowledgeRow(r: any): KnowledgeRow {
    const count = (value: any) => Number(value) || 0;
    const time = (value: any) => Number(value) || null;
    return {
        id: r.id,
        name: r.name || r.id,
        description: r.description || '',
        status: r.status || '',
        sourceType: r.source_type || '',
        documentCount: count(r.document_count),
        chunkCount: count(r.chunk_count),
        maxPages: Number(r.max_pages) || null,
        startedAt: time(r.started_at),
        completedAt: time(r.completed_at),
        errorMessage: r.error_message || '',
    };
}

function isBuilding(kb: KnowledgeRow): boolean {
    return KB_BUILDING.includes(kb.status);
}

/** 45s, 6m 12s, 1h 3m. */
function duration(ms: number): string {
    const seconds = Math.max(0, Math.round(ms / 1000));
    if (seconds < 60) {
        return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    return minutes < 60 ? `${minutes}m ${seconds % 60}s` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** The start URLs, one per line (commas too, as the gateway reads them). */
function urlLines(value: string): string[] {
    return (value || '').split(/[\n,]/).map(u => u.trim()).filter(Boolean);
}

/** The site's name for a default title: www.karexpert.com/about -> karexpert.com. */
function siteName(url: string): string {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return '';
    }
}

/** Material icon shapes (24x24), inlined: this app has no icon font with them. */
const ICON_PATHS: Record<string, string> = {
    check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
    back: 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z',
    next: 'M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z',
    close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
    add: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
    play: 'M8 5v14l11-7z',
    website: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
    file: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
    text: 'M14 17H4v2h10v-2zm6-8H4v2h16V9zM4 15h16v-2H4v2zM4 5v2h16V5H4z',
    mongodb: 'M12 3C7.58 3 4 4.79 4 7v10c0 2.21 3.59 4 8 4s8-1.79 8-4V7c0-2.21-3.58-4-8-4zm0 2c3.87 0 6 1.5 6 2s-2.13 2-6 2-6-1.5-6-2 2.13-2 6-2zm6 12c0 .5-2.13 2-6 2s-6-1.5-6-2v-2.23c1.61.78 3.72 1.23 6 1.23s4.39-.45 6-1.23V17zm0-4.5c0 .5-2.13 2-6 2s-6-1.5-6-2v-2.23c1.61.78 3.72 1.23 6 1.23s4.39-.45 6-1.23v2.23z',
};

/** The Knowledge step's "create a new source" choices. */
const KB_SOURCE_CHOICES: Array<{ id: KnowledgeSource; title: string; description: string }> = [
    { id: 'website', title: 'Website', description: 'Crawl pages from a site' },
    { id: 'file', title: 'Files', description: 'PDF, Word, Excel, CSV or text' },
    { id: 'text', title: 'Text', description: 'Paste a policy or an FAQ' },
    { id: 'mongodb', title: 'MongoDB', description: 'Embed a collection' },
];

function slugify(value: string): string {
    return (value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** The gateway's answer for one resource: the first row of `resource`. */
function firstRow(data: any): any {
    return Array.isArray(data?.resource) ? data.resource[0] : data?.resource;
}

/**
 * The guided agent wizard ("Create agent"). A setup is an ai_guided_setup master: every Next
 * saves it (the gateway rebuilds its graph from the fields), Test runs that graph on the
 * domain runtime, and Publish keeps it as a new immutable version in the setup itself.
 * Opened on an account's copy (owner_account_id set), it edits that copy only: its identity and
 * version are fixed, and it is run from the Agents page, not tested or published here.
 */
@Component({
    selector: 'app-ai-guided-setup-wizard',
    templateUrl: './ai-guided-setup-wizard.component.html',
    styleUrls: ['./ai-guided-setup-wizard.component.scss'],
    standalone: false
})
export class AiGuidedSetupWizardComponent implements OnInit, OnDestroy {

    readonly steps = STEPS;
    readonly stepTitles = STEPS.map(s => s.title);
    readonly sourceChoices = KB_SOURCE_CHOICES;
    /** Opened on a saved setup (Edit setup, New version), not a new one. */
    resumed = false;

    /** The Triggers step's editor; there once that step has been opened (it then stays, hidden). */
    @ViewChild(AgentTriggerEditorComponent) triggerEditor?: AgentTriggerEditorComponent;
    triggersVisited = false;
    /** Stored triggers of the setup, for the Publish summary when the Triggers step was not opened. */
    storedTriggerCount = 0;
    sampling = false;

    step = 1;
    draftId: string | null = null;
    saving = false;
    testing = false;
    publishing = false;
    error = '';

    domains: DomainOption[] = [];
    /** Distinct llm_name values of ai_model_master. */
    providers: string[] = [];
    private allModels: ModelOption[] = [];
    /** The Tool Registry entry picked to add (its slug), and its row. */
    registryToolPick = '';
    private registryToolPicked: ToolRow | null = null;
    /** The knowledge base picked to add (its id), and its row. */
    knowledgePick = '';
    private knowledgePicked: KnowledgeRow | null = null;

    // ---- searchselect (kx-multiselect-dropdown) lists; a list that changes gets a new object ----
    readonly selectSettings = SINGLE_SELECT;
    domainOptions = optionsOf([]);
    providerOptions = optionsOf([]);
    modelOptions = optionsOf([]);
    readonly lifecycleOptions = optionsOf(LIFECYCLES.map(l => ({ id: l, text: l })));
    readonly methodOptions = optionsOf(HTTP_METHODS.map(m => ({ id: m, text: m })));
    readonly domainQuestion = listQuestion('wizard_domain');
    readonly providerQuestion = listQuestion('wizard_provider');
    readonly modelQuestion = listQuestion('wizard_model');
    readonly lifecycleQuestion = listQuestion('wizard_lifecycle');
    /** Shared by every tool row's Method: the same fixed list. */
    readonly methodQuestion = listQuestion('wizard_tool_method');
    /** The caller's account's active Tool Registry entries, searched on the gateway a page at a time. */
    readonly toolSettings = SERVER_SEARCH_SELECT;
    readonly toolSource = {
        application: APPLICATION,
        resource: TOOL_RESOURCE,
        query_id: 'GET_PAGE',
        filter: 'status:active',
        search_columns: 'name,slug',
        page_no: 0,
        page_size: 20,
    };
    readonly toolQuestion = {
        key: 'wizard_registry_tool',
        getterKey: 'slug',
        getterValue: 'name',
        req_keys: ['name', 'slug', 'description', 'url', 'method'],
    };
    /**
     * The knowledge bases the caller may use (ai_knowledge_base is account-aware: an account
     * user sees their account's), searched on the gateway a page at a time.
     */
    readonly knowledgeSource = {
        application: APPLICATION,
        resource: KNOWLEDGE_RESOURCE,
        query_id: 'GET_PAGE',
        search_columns: 'name',
        page_no: 0,
        page_size: 20,
    };
    readonly knowledgeQuestion = {
        key: 'wizard_knowledge_base',
        getterKey: 'id',
        getterValue: 'name',
        req_keys: ['id', 'name', 'description', 'status', 'source_type', 'document_count', 'chunk_count', 'max_pages',
            'started_at', 'completed_at', 'error_message'],
    };
    /** The source of the knowledge base being created in the Knowledge step, if any. */
    newSource: KnowledgeSource | null = null;
    readonly fileFormats = KB_FILE_FORMATS;
    readonly maxTextChars = KB_MAX_TEXT_CHARS;
    /** Files being uploaded to DMS: the knowledge base is created once none is. */
    uploadingFiles = 0;
    knowledgeForm = blankKnowledgeForm();
    /** The name and description are filled from the website until the user edits them. */
    private knowledgeNameEdited = false;
    private knowledgeDescriptionEdited = false;
    creatingKnowledge = false;
    knowledgeError = '';
    /** Re-reads the knowledge bases still building; null when none is. */
    private buildWatch: Subscription | null = null;
    /** When the building rows were last read: their elapsed times count to it. */
    clock = Date.now();


    // Identity
    domain = '';
    agentName = '';
    displayName = '';

    // Instructions
    goal = '';
    instructions = '';

    // Model
    providerId = '';
    providerName = '';
    modelId = '';
    modelName = '';
    /** A key typed in this session; blank keeps the saved one. */
    apiKey = '';
    /** The saved key as the gateway shows it (masked: only its last characters). */
    apiKeySaved = '';
    /** Remove the saved key on the next save: runs then use third_party_app_credentials. */
    clearApiKey = false;
    temperature = 0.5;

    // Tools
    tools: ToolRow[] = [];

    // Knowledge
    knowledgeBases: KnowledgeRow[] = [];
    knowledgeOnly = false;

    // Guardrails
    guardrailsEnabled = false;
    guardrailsConfigId = 'default';

    // Memory
    memoryEnabled = false;
    memoryKey = '';
    memoryMaxEntries = 5;

    // Test
    testMessage = '';
    testResult: string | null = null;

    // Publish
    version = 'v1';
    lifecycle = 'stateless';
    previouslyPublishedVersion: string | null = null;
    /** Editing an account's copy of a published version, not a master. */
    copyMode = false;
    /** The copy's account: a knowledge base created for the copy is that account's. */
    private ownerAccountId = '';

    constructor(
        private readonly baseService: BaseService,
        private readonly alerts: AlertsService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    ) { }

    ngOnInit(): void {
        this.loadModels();
        const draftId = this.route.snapshot.queryParamMap.get('draftId');
        if (draftId) {
            this.draftId = draftId;
            this.resumed = true;
            this.loadDraft(draftId);
        } else {
            this.loadDomains();
        }
    }

    ngOnDestroy(): void {
        this.stopWatchingBuilds();
    }

    get systemPrompt(): string {
        return (this.instructions || '').trim() || (this.goal || '').trim();
    }

    get validFinalStep(): boolean {
        return !!(this.domain && this.agentName.trim() && this.displayName.trim()
            && this.goal.trim() && this.version.trim() && this.lifecycle);
    }

    /** The active domains (AI Applications): the ones the agent can be published into. */
    private loadDomains(): void {
        this.baseService.getData({}, '', DOMAIN_RESOURCE, APPLICATION, 'GET_MAPPED_DOMAINS').subscribe({
            next: (data: any) => {
                if (data?.errCode === 0 && Array.isArray(data.resource)) {
                    this.domains = data.resource.map((r: any) => ({ id: r.domain, displayName: r.display_name }));
                    this.domainOptions = optionsOf(this.domains.map(d => ({ id: d.id, text: `${d.displayName} (${d.id})` })));
                    if (!this.domain && this.domains.length > 0) {
                        this.domain = this.domains[0].id;
                    }
                }
            },
        });
    }

    /** ai_model_master, the same model list AI Prompt Master picks from. */
    private loadModels(): void {
        this.baseService.getData({ page_no: 0, page_size: 500 }, '', MODEL_RESOURCE, APPLICATION, 'GET_PAGE').subscribe({
            next: (data: any) => {
                if (data?.errCode !== 0) {
                    this.error = data?.message || 'Could not load the models.';
                    return;
                }
                this.allModels = (Array.isArray(data.resource) ? data.resource : [])
                    .filter((r: any) => r.llm_name && r.model_name)
                    .map((r: any) => ({ id: r.id, llmName: String(r.llm_name).trim(), name: String(r.model_name).trim() }));
                this.providers = Array.from(new Set(this.allModels.map(m => m.llmName))).sort();
                this.providerOptions = optionsOf(this.providers.map(p => ({ id: p, text: p })));
                if (this.providerName) {
                    this.selectProvider(this.providerName, this.modelName);   // a resumed draft's choice
                } else if (this.providers.length) {
                    this.selectProvider(this.providers[0]);
                }
            },
        });
    }

    /** The models of the selected provider. */
    get models(): ModelOption[] {
        return this.allModels.filter(m => m.llmName === this.providerName);
    }

    /** Picks a provider (an llm_name), keeping `preferredModelName` when it is one of its models. */
    selectProvider(llmName: string, preferredModelName?: string): void {
        this.providerId = llmName;
        this.providerName = llmName;
        this.modelOptions = optionsOf(this.models.map(m => ({ id: m.id, text: m.name })));
        const keep = this.models.find(m => m.name === preferredModelName || m.id === preferredModelName);
        const target = keep || this.models[0];
        if (target) {
            this.selectModel(target.id);
        } else {
            this.modelId = '';
            this.modelName = '';
        }
    }

    selectModel(modelId: string): void {
        const model = this.allModels.find(m => m.id === modelId);
        this.modelId = modelId;
        this.modelName = model?.name || '';
    }

    private loadDraft(draftId: string): void {
        this.baseService.getById(draftId, GUIDED_RESOURCE, APPLICATION).subscribe({
            next: (r: any) => {
                if (!r) {
                    this.error = 'Could not load the guided setup.';
                    return;
                }
                this.domain = r.domain || this.domain;
                this.agentName = r.agent_name || '';
                this.displayName = r.display_name || '';
                this.goal = r.goal || '';
                this.instructions = r.instructions || '';
                this.providerName = r.provider_name || '';
                this.modelName = r.model_name || '';
                this.apiKeySaved = r.api_key || '';
                this.temperature = r.temperature ? Number(r.temperature) : 0.5;
                this.tools = Array.isArray(r.tools) ? r.tools : [];
                this.loadKnowledgeBases(Array.isArray(r.knowledge_base_ids) ? r.knowledge_base_ids : []);
                this.knowledgeOnly = !!r.knowledge_only;
                this.guardrailsEnabled = !!r.guardrails_enabled;
                this.guardrailsConfigId = r.guardrails_config_id || 'default';
                this.memoryEnabled = !!r.memory_enabled;
                this.memoryKey = r.memory_key || '';
                this.memoryMaxEntries = r.memory_max_entries ? Number(r.memory_max_entries) : 5;
                this.version = r.version || 'v1';
                this.lifecycle = r.lifecycle || 'stateless';
                this.copyMode = !!r.owner_account_id;
                this.ownerAccountId = r.owner_account_id || '';
                const published = Array.isArray(r.published_versions) ? r.published_versions : [];
                this.previouslyPublishedVersion = published.length ? published[published.length - 1]?.version || null : null;
                this.providerId = r.provider_id || this.providerName;
                this.modelId = r.model_id || '';
                if (this.allModels.length && this.providerName) {
                    this.selectProvider(this.providerName, this.modelName);
                }
                this.loadDomains();
            },
            error: () => { this.error = 'Could not load the guided setup.'; },
        });
    }

    iconPath(name: string): string {
        return ICON_PATHS[name] || '';
    }

    get apiKeyHint(): string {
        if (this.apiKeySaved) {
            const last = this.apiKeySaved.replace(/^X+/, '');
            return `A key ending in ${last || '…'} is saved; leave blank to keep it.`;
        }
        if (this.clearApiKey) {
            return 'The saved key is removed when you save.';
        }
        return 'Optional: without one, each run uses the key Third Party App Credentials holds for its account.';
    }

    /** Takes effect on the next save; typing a new key instead saves that one. */
    removeSavedKey(): void {
        this.apiKeySaved = '';
        this.apiKey = '';
        this.clearApiKey = true;
    }

    get stepKey(): StepKey {
        return STEPS[this.step - 1].key;
    }

    get currentStep(): StepDef {
        return STEPS[this.step - 1];
    }

    get pageTitle(): string {
        return this.copyMode ? 'Edit account copy' : this.resumed ? 'Edit agent' : 'Create agent';
    }

    /** The chosen domain's name, for the Publish summary. */
    get domainLabel(): string {
        return this.domains.find(d => d.id === this.domain)?.displayName || this.domain;
    }

    /** The ID publishing gives the agent, unless the gateway names it. */
    get agentIdPreview(): string {
        const slug = slugify(this.agentName);
        return this.domain && slug ? `${this.domain}.${slug}` : '';
    }

    /** The Publish summary's links back to a step. */
    openStep(key: StepKey): void {
        this.selectStep(STEPS.findIndex(s => s.key === key) + 1);
    }

    /** Triggers stored or being edited for this setup. */
    get triggerCount(): number {
        return this.triggerEditor ? this.triggerEditor.count : this.storedTriggerCount;
    }

    /** Whether a step already holds something: drives the step tabs' check marks. */
    stepHasContent(stepNumber: number): boolean {
        switch (STEPS[stepNumber - 1]?.key) {
            case 'identity':
                return !!(this.domain && this.agentName.trim() && this.displayName.trim());
            case 'instructions':
                return !!this.goal.trim();
            case 'model':
                return !!(this.providerId && this.modelId);
            case 'tools':
                return this.tools.length > 0;
            case 'knowledge':
                return this.knowledgeBases.length > 0;
            case 'triggers':
                return this.triggerCount > 0;
            case 'guardrails':
                return this.guardrailsEnabled;
            case 'memory':
                return this.memoryEnabled;
            case 'test':
                return !!this.testResult;
            default:
                return false;
        }
    }

    addTool(): void {
        this.tools.push({ name: '', slug: '', description: '', url: '', method: 'GET' });
    }

    /** The Tool Registry entry picked (onSelect's row), or none once cleared. */
    pickRegistryTool(pick: any): void {
        const r = pick?.alldata;
        this.registryToolPick = r?.slug || '';
        this.registryToolPicked = r?.slug
            ? { name: r.name, slug: r.slug, description: r.description || '', url: r.url, method: r.method || 'GET' }
            : null;
    }

    addToolFromRegistry(): void {
        const tool = this.registryToolPicked;
        if (tool) {
            // A copy: editing the registry later does not change this agent.
            this.tools.push({ ...tool });
        }
    }

    /** The knowledge base picked (onSelect's row), or none once cleared. */
    pickKnowledgeBase(pick: any): void {
        const r = pick?.alldata;
        this.knowledgePick = r?.id || '';
        this.knowledgePicked = r?.id ? knowledgeRow(r) : null;
    }

    addKnowledgeBase(): void {
        const kb = this.knowledgePicked;
        // A reference, unlike a tool: the agent searches the knowledge base as it is rebuilt.
        if (kb && !this.knowledgeBases.some(k => k.id === kb.id)) {
            this.knowledgeBases.push({ ...kb });
            this.watchBuilds();
        }
    }

    /** Only takes it off this agent: the knowledge base stays in Knowledge Bases. */
    removeKnowledgeBase(index: number): void {
        this.knowledgeBases.splice(index, 1);
    }

    /** A saved setup keeps only the ids: each row's name, description and status are read back. */
    private loadKnowledgeBases(ids: string[]): void {
        this.knowledgeBases = ids.map(id => knowledgeRow({ id }));
        for (const row of this.knowledgeBases) {
            this.baseService.getById(row.id, KNOWLEDGE_RESOURCE, APPLICATION).subscribe({
                next: (r: any) => {
                    if (r) {
                        Object.assign(row, knowledgeRow(r));
                        this.watchBuilds();
                    }
                },
                // Gone, or another account's: the id stays, and saving tells the user.
                error: () => { row.status = 'not found'; },
            });
        }
    }

    // ---- New knowledge bases, created from the Knowledge step ----

    openNewSource(source: KnowledgeSource): void {
        this.newSource = source;
        this.knowledgeForm = blankKnowledgeForm();
        this.knowledgeNameEdited = false;
        this.knowledgeDescriptionEdited = false;
        this.knowledgeError = '';
    }

    closeNewSource(): void {
        this.newSource = null;
        // Not kept once the form closes: the connection string is a secret.
        this.knowledgeForm = blankKnowledgeForm();
        this.knowledgeError = '';
    }

    /** Names and describes a website knowledge base after its first start URL, until the user does. */
    onWebsiteUrlsChange(): void {
        const site = siteName(urlLines(this.knowledgeForm.websiteUrls)[0] || '');
        if (!site) {
            return;
        }
        if (!this.knowledgeNameEdited) {
            this.knowledgeForm.name = `${site} website`;
        }
        if (!this.knowledgeDescriptionEdited) {
            this.knowledgeForm.description = `Public web pages of ${site}`;
        }
    }

    /** Uploads the chosen files to DMS, as the platform's file uploader does. */
    onKnowledgeFilesChosen(event: Event): void {
        const input = event.target as HTMLInputElement;
        const chosen = Array.from(input.files || []);
        // So the same file can be chosen again after it is removed.
        input.value = '';
        this.knowledgeError = '';
        for (const file of chosen) {
            if (this.knowledgeForm.files.length + this.uploadingFiles >= KB_MAX_FILES) {
                this.knowledgeError = `A knowledge base takes at most ${KB_MAX_FILES} files.`;
                break;
            }
            if (file.size > KB_MAX_FILE_BYTES) {
                this.knowledgeError = `${file.name} is larger than 15 MB.`;
                continue;
            }
            this.uploadKnowledgeFile(file);
        }
    }

    private uploadKnowledgeFile(file: File): void {
        this.uploadingFiles += 1;
        const reader = new FileReader();
        reader.onerror = () => {
            this.uploadingFiles -= 1;
            this.knowledgeError = `Could not read ${file.name}.`;
        };
        reader.onload = () => {
            const form = new FormData();
            form.append('file', file, file.name);
            form.append('fileName', file.name);
            form.append('action', 'add');
            form.append('userId', '');
            // The file's raw content signs the upload's XSRF token, as with the platform's uploader.
            this.baseService.upload(form, 'dms', KB_DOCUMENT_TYPE, reader.result).pipe(
                // upload never completes when its request fails.
                timeout(KB_UPLOAD_TIMEOUT_MS),
                finalize(() => { this.uploadingFiles -= 1; }),
            ).subscribe({
                next: (data: any) => {
                    if (data?.errCode === -1 || !data?.documentId) {
                        this.knowledgeError = data?.message || `Could not upload ${file.name}.`;
                        return;
                    }
                    this.knowledgeForm.files.push({ documentId: data.documentId, documentName: data.documentName || file.name });
                    this.onKnowledgeFilesChange();
                },
                error: (error: unknown) => { this.knowledgeError = this.errorMessage(error, `Could not upload ${file.name}.`); },
            });
        };
        reader.readAsBinaryString(file);
    }

    /** Only takes the file off this form: the uploaded document stays in DMS. */
    removeKnowledgeFile(index: number): void {
        this.knowledgeForm.files.splice(index, 1);
        this.onKnowledgeFilesChange();
    }

    /** Names and describes a file knowledge base after its files, until the user does. */
    private onKnowledgeFilesChange(): void {
        const names = this.knowledgeForm.files.map(f => f.documentName);
        if (!names.length) {
            return;
        }
        if (!this.knowledgeNameEdited) {
            this.knowledgeForm.name = names[0].replace(/\.[^.]+$/, '');
        }
        if (!this.knowledgeDescriptionEdited) {
            this.knowledgeForm.description = `Documents: ${names.join(', ')}`.slice(0, 1000);
        }
    }

    knowledgeSourceLabel(kb: KnowledgeRow): string {
        return KB_SOURCE_LABELS[kb.sourceType] || kb.sourceType;
    }

    onKnowledgeNameChange(): void {
        this.knowledgeNameEdited = true;
    }

    onKnowledgeDescriptionChange(): void {
        this.knowledgeDescriptionEdited = true;
    }

    /** What the form still needs; the gateway checks the rest (limits, URL shapes) when it saves. */
    private knowledgeFormProblem(): string {
        const f = this.knowledgeForm;
        if (!f.name.trim() || !f.description.trim()) {
            return 'A name and a description are required: agents pick which knowledge base to search by its description.';
        }
        if (this.newSource === 'website') {
            return urlLines(f.websiteUrls).length ? '' : 'Enter at least one start URL.';
        }
        if (this.newSource === 'text') {
            const length = f.textContent.trim().length;
            return !length ? 'Enter the text to search.'
                : length > KB_MAX_TEXT_CHARS ? `The text can be at most ${KB_MAX_TEXT_CHARS.toLocaleString()} characters; upload longer content as a file.` : '';
        }
        if (this.newSource === 'file') {
            return this.uploadingFiles ? 'Wait for the files to finish uploading.' : f.files.length ? '' : 'Upload at least one file.';
        }
        if (!f.connectionString.trim() || !f.dbName.trim() || !f.collectionName.trim() || !f.fields.trim()) {
            return 'The connection string, database, collection and fields to embed are required.';
        }
        return '';
    }

    private knowledgePayload(): any {
        const f = this.knowledgeForm;
        const name = f.name.trim();
        const payload: any = {
            name,
            description: f.description.trim(),
            source_type: this.newSource,
            embedding: KB_EMBEDDING,
            embedding_dimensions: KB_EMBEDDING_DIMENSIONS,
            // Its own collection, as the knowledge base screen asks for one; the suffix keeps two of the same name apart.
            embedding_collection: `${slugify(name).replace(/-/g, '_').slice(0, 60) || 'knowledge'}_kb_${Math.random().toString(36).slice(2, 7)}`,
        };
        if (this.newSource === 'website') {
            Object.assign(payload, {
                website_urls: urlLines(f.websiteUrls).join('\n'),
                max_depth: Number(f.maxDepth),
                max_pages: Number(f.maxPages),
                refresh_hours: Number(f.refreshHours) || 0,
                include_patterns: f.includePatterns.trim(),
                exclude_patterns: f.excludePatterns.trim(),
            });
        } else if (this.newSource === 'text') {
            payload.text_content = f.textContent.trim();
        } else if (this.newSource === 'file') {
            payload.files = f.files.map(file => ({ ...file }));
        } else {
            Object.assign(payload, {
                connection_string: f.connectionString.trim(),
                db_name: f.dbName.trim(),
                collection_name: f.collectionName.trim(),
                fields: f.fields.trim(),
                title_field: f.titleField.trim(),
                filter: f.filter.trim(),
            });
        }
        if (this.copyMode && this.ownerAccountId) {
            // The copy runs as its account, which may search only global knowledge bases and its own.
            payload.account_id = this.ownerAccountId;
        }
        return payload;
    }

    /** Saves the knowledge base (the gateway starts its build) and adds it to the agent. */
    createKnowledgeBase(): void {
        if (this.creatingKnowledge) {
            return;
        }
        this.knowledgeError = this.knowledgeFormProblem();
        if (this.knowledgeError) {
            return;
        }
        this.creatingKnowledge = true;
        this.baseService.add(this.knowledgePayload(), KNOWLEDGE_RESOURCE, true, APPLICATION).pipe(
            finalize(() => { this.creatingKnowledge = false; }),
        ).subscribe({
            next: (data: any) => {
                const created = data?.errCode === 0 ? firstRow(data) : null;
                if (!created?.id) {
                    this.knowledgeError = data?.message || 'Could not create the knowledge base.';
                    return;
                }
                this.knowledgeBases.push(knowledgeRow({ ...created, status: created.status || 'pending' }));
                this.closeNewSource();
                this.watchBuilds();
            },
            error: (error: unknown) => { this.knowledgeError = this.errorMessage(error, 'Could not create the knowledge base.'); },
        });
    }

    /** Saves the knowledge base again as it is, which rebuilds it (the stored connection string is kept). */
    retryKnowledgeBase(kb: KnowledgeRow): void {
        this.error = '';
        this.baseService.action({ id: kb.id }, 'MODIFY', APPLICATION, KNOWLEDGE_RESOURCE).subscribe({
            next: (data: any) => {
                if (data?.errCode !== 0) {
                    this.error = data?.message || 'Could not rebuild the knowledge base.';
                    return;
                }
                Object.assign(kb, { status: 'pending', errorMessage: '', startedAt: null, completedAt: null });
                this.watchBuilds();
            },
            error: (error: unknown) => { this.error = this.errorMessage(error, 'Could not rebuild the knowledge base.'); },
        });
    }

    // ---- Build progress ----

    isBuilding(kb: KnowledgeRow): boolean {
        return isBuilding(kb);
    }

    /**
     * Reads the building knowledge bases again every few seconds until none is building. The
     * build runs on the engine whether or not this page is open.
     */
    private watchBuilds(): void {
        this.clock = Date.now();
        if (this.buildWatch || !this.knowledgeBases.some(isBuilding)) {
            return;
        }
        this.buildWatch = timer(KB_POLL_MS, KB_POLL_MS).pipe(
            exhaustMap(() => forkJoin(this.knowledgeBases.filter(isBuilding).map(kb => this.refreshKnowledgeBase(kb)))
                .pipe(defaultIfEmpty([]))),
        ).subscribe(() => {
            this.clock = Date.now();
            if (!this.knowledgeBases.some(isBuilding)) {
                this.stopWatchingBuilds();
            }
        });
    }

    private stopWatchingBuilds(): void {
        this.buildWatch?.unsubscribe();
        this.buildWatch = null;
    }

    private refreshKnowledgeBase(kb: KnowledgeRow): Observable<void> {
        return this.baseService.getById(kb.id, KNOWLEDGE_RESOURCE, APPLICATION).pipe(
            // getById never completes when its request fails, which would stall every later tick.
            timeout(KB_READ_TIMEOUT_MS),
            map((r: any) => {
                if (r) {
                    Object.assign(kb, knowledgeRow(r));
                } else {
                    kb.status = 'not found';
                }
            }),
            // A failed read is tried again on the next tick.
            catchError(() => of(undefined)),
        );
    }

    /** The card's state: drives its chip's label and colour. */
    knowledgeState(kb: KnowledgeRow): string {
        switch (kb.status) {
            case 'pending':
                return 'queued';
            case 'processing':
                return 'building';
            case 'completed':
                return kb.errorMessage ? 'warning' : 'ready';
            case 'failed':
                return 'failed';
            case 'not found':
                return 'missing';
            default:
                return 'loading';
        }
    }

    knowledgeStateLabel(kb: KnowledgeRow): string {
        const labels: Record<string, string> = {
            queued: 'Queued', building: 'Building', ready: 'Ready', warning: 'Ready, with a warning',
            failed: 'Failed', missing: 'Not found', loading: '…',
        };
        return labels[this.knowledgeState(kb)];
    }

    /** What a build has done so far, or what the finished one holds. */
    knowledgeProgress(kb: KnowledgeRow): string {
        const website = kb.sourceType === 'website';
        // A file knowledge base counts each PDF page (and spreadsheet sheet) as one.
        const unit = website ? 'pages' : kb.sourceType === 'file' ? 'pages and files' : kb.sourceType === 'text' ? 'texts' : 'documents';
        const chunks = `${kb.chunkCount.toLocaleString()} chunks`;
        switch (kb.status) {
            case 'pending':
                return 'Waiting to start: builds run a few at a time.';
            case 'processing': {
                const elapsed = kb.startedAt ? ` · ${duration(this.clock - kb.startedAt)}` : '';
                if (!kb.documentCount) {
                    const starting: Record<string, string> = {
                        website: 'Starting: reading robots.txt, the sitemap and the start pages',
                        file: 'Starting: reading the files', text: 'Starting: reading the text',
                    };
                    return (starting[kb.sourceType] || 'Starting: reading the collection') + elapsed;
                }
                const read = website && kb.maxPages
                    ? `${kb.documentCount.toLocaleString()} of up to ${kb.maxPages.toLocaleString()} pages`
                    : `${kb.documentCount.toLocaleString()} ${unit}`;
                return `${read} read · ${chunks} so far${elapsed}`;
            }
            case 'completed': {
                const took = kb.startedAt && kb.completedAt ? ` · built in ${duration(kb.completedAt - kb.startedAt)}` : '';
                return `${kb.documentCount.toLocaleString()} ${unit} · ${chunks}${took}`;
            }
            default:
                return '';
        }
    }

    /** The knowledge bases a publish (or a copy's save) would refuse: every one must be built. */
    get knowledgeNotReady(): KnowledgeRow[] {
        return this.knowledgeBases.filter(kb => kb.status !== 'completed');
    }

    get knowledgeNotReadyNames(): string {
        return this.knowledgeNotReady.map(kb => `${kb.name} (${this.knowledgeStateLabel(kb)})`).join(', ');
    }

    removeTool(index: number): void {
        this.tools.splice(index, 1);
    }

    onToolNameChange(tool: ToolRow): void {
        tool.slug = slugify(tool.name);
    }

    /** Any step can be opened, as in the reference wizard. */
    selectStep(target: number): void {
        if (target < 1 || target > this.stepTitles.length) {
            return;
        }
        this.goTo(target);
        this.error = '';
    }

    back(): void {
        if (this.step > 1) {
            this.goTo(this.step - 1);
        }
        this.error = '';
    }

    /** Opening Triggers creates its editor (which loads the catalog then); Publish needs the trigger count. */
    private goTo(target: number): void {
        this.step = target;
        if (this.stepKey === 'triggers') {
            this.triggersVisited = true;
        }
        if (this.stepKey === 'publish' && !this.triggersVisited && this.draftId && !this.copyMode) {
            this.loadStoredTriggerCount();
        }
    }

    private loadStoredTriggerCount(): void {
        // Only the total is needed: one row is enough to get GET_PAGE's count.
        this.baseService.getData({ args: 'source_setup_id:' + this.draftId, page_no: 0, page_size: 1 }, '', TRIGGER_RESOURCE, APPLICATION, 'GET_PAGE').subscribe({
            next: (data: any) => {
                this.storedTriggerCount = data?.errCode === 0 ? Number(data.count) || 0 : 0;
            },
        });
    }

    next(): void {
        if (this.saving) {
            return;
        }
        this.error = '';
        if (this.stepKey === 'identity' && !(this.domain && this.agentName.trim() && this.displayName.trim())) {
            this.error = 'Domain, agent name, and display name are required.';
            return;
        }
        if (this.stepKey === 'instructions' && !this.goal.trim()) {
            this.error = 'Goal is required.';
            return;
        }
        if (this.stepKey === 'model' && !(this.providerId && this.modelId)) {
            this.error = 'Provider and model are required.';
            return;
        }
        if (this.step >= this.stepTitles.length) {
            return;
        }
        this.saveDraft().subscribe({
            next: () => { this.goTo(this.step + 1); },
            error: (error: unknown) => { this.error = this.errorMessage(error, 'Could not save the guided setup.'); },
        });
    }

    private errorMessage(error: unknown, fallback: string): string {
        if (typeof error === 'string' && error.trim()) {
            return error;
        }
        if (error instanceof HttpErrorResponse) {
            const body: unknown = error.error;
            if (typeof body === 'string' && body.trim()) {
                return body;
            }
            if (typeof body === 'object' && body !== null && 'message' in body
                && typeof body.message === 'string' && body.message.trim()) {
                return body.message;
            }
        }
        return error instanceof Error && error.message ? error.message : fallback;
    }

    private buildPayload(): any {
        const payload: any = {
            domain: this.domain,
            agent_name: this.agentName.trim(),
            display_name: this.displayName.trim(),
            goal: this.goal.trim(),
            instructions: this.instructions.trim(),
            provider_id: this.providerId,
            provider_name: this.providerName,
            model_id: this.modelId,
            model_name: this.modelName,
            temperature: String(this.temperature),
            tools: this.tools.filter(t => t.name.trim() && t.url.trim()),
            knowledge_base_ids: this.knowledgeBases.map(k => k.id),
            knowledge_only: this.knowledgeOnly,
            guardrails_enabled: this.guardrailsEnabled,
            guardrails_config_id: this.guardrailsConfigId,
            memory_enabled: this.memoryEnabled,
            memory_key: this.memoryKey,
            memory_max_entries: String(this.memoryMaxEntries),
            version: this.version.trim(),
            lifecycle: this.lifecycle,
        };
        if (this.apiKey.trim()) {
            // Only a newly typed key is sent; the gateway keeps the saved one otherwise.
            payload.api_key = this.apiKey.trim();
        } else if (this.clearApiKey) {
            payload.extra_data = { clear_api_key: true };
        }
        if (this.draftId) {
            payload.id = this.draftId;
        }
        return payload;
    }

    /**
     * Creates the setup on its first save, otherwise saves the latest values to it, and
     * emits its id. Fails with the gateway's message, so callers never act on a stale draft.
     */
    saveDraft(): Observable<string> {
        this.saving = true;
        const payload = this.buildPayload();
        const request = this.draftId
            ? this.baseService.action(payload, 'MODIFY', APPLICATION, GUIDED_RESOURCE)
            : this.baseService.add(payload, GUIDED_RESOURCE, true, APPLICATION);
        return request.pipe(
            switchMap((data: any) => {
                if (data?.errCode !== 0) {
                    return throwError(() => data?.message || 'Could not save the guided setup.');
                }
                const created = firstRow(data);
                if (!this.draftId && created?.id) {
                    this.draftId = created.id;
                }
                if (this.apiKey.trim()) {
                    this.apiKeySaved = 'XXXX' + this.apiKey.trim().slice(-4);
                    this.apiKey = '';
                }
                this.clearApiKey = false;
                return this.draftId ? of(this.draftId) : throwError(() => 'Could not save the guided setup.');
            }),
            // The setup's triggers, once their step was opened: stored before Test or Publish use them.
            switchMap((id: string) => this.triggerEditor
                ? this.triggerEditor.sync(id).pipe(map(() => id))
                : of(id)),
            finalize(() => { this.saving = false; }),
        );
    }

    runTest(): void {
        if (this.testing || !this.testMessage.trim()) {
            return;
        }
        this.testing = true;
        this.error = '';
        this.testResult = null;
        this.saveDraft().pipe(
            switchMap(id => this.baseService.action({ id, extra_data: { message: this.testMessage.trim() } },
                'EXECUTE_DRAFT', APPLICATION, GUIDED_RESOURCE)),
            map((data: any) => {
                if (data?.errCode !== 0) {
                    throw data?.message || 'The test run failed.';
                }
                return firstRow(data)?.extra_data?.test_result || 'No response.';
            }),
        ).subscribe({
            next: (reply: string) => { this.testing = false; this.testResult = reply; },
            error: (error: unknown) => { this.testing = false; this.error = this.errorMessage(error, 'The test run failed.'); },
        });
    }

    /** Fills the test message from a trigger's latest event (TEST_TRIGGER), as the agent would receive it. */
    useTriggerSample(row: TriggerRow): void {
        if (!this.triggerEditor || this.sampling) {
            return;
        }
        this.sampling = true;
        this.error = '';
        this.triggerEditor.testTrigger(row).subscribe({
            next: result => {
                this.sampling = false;
                if (!result.matched) {
                    this.error = `The latest event does not meet ${row.name || 'this trigger'}'s conditions, so the agent would not run.`;
                    return;
                }
                this.testMessage = result.message;
            },
            error: (error: unknown) => { this.sampling = false; this.error = this.errorMessage(error, 'Could not test the trigger.'); },
        });
    }

    publish(): void {
        if (!this.validFinalStep || this.publishing) {
            return;
        }
        this.publishing = true;
        this.error = '';
        this.saveDraft().pipe(
            switchMap(id => this.baseService.action({ id }, 'PUBLISH', APPLICATION, GUIDED_RESOURCE)),
            map((data: any) => {
                if (data?.errCode !== 0) {
                    throw data?.message || 'Could not publish the agent.';
                }
                return firstRow(data)?.extra_data?.agent_id || `${this.domain}.${slugify(this.agentName)}`;
            }),
        ).subscribe({
            next: (agentId: string) => {
                this.publishing = false;
                this.alerts.success(`Published ${agentId}@${this.version.trim()}.`, '', true);
                this.router.navigate(['/aiml-gateway/ai-agent-registry'], { queryParams: { tab: 'registry' } });
            },
            error: (error: unknown) => {
                this.publishing = false;
                this.error = this.errorMessage(error, 'Could not publish the agent.');
            },
        });
    }

    /** Copy mode's final step: saves the copy; its account keeps running the new values. */
    saveCopy(): void {
        if (this.saving) {
            return;
        }
        this.error = '';
        this.saveDraft().subscribe({
            next: () => {
                this.alerts.success('Agent copy saved.', '', true);
                this.router.navigate(['/aiml-gateway/ai-agent-registry'], { queryParams: { tab: 'accounts' } });
            },
            error: (error: unknown) => { this.error = this.errorMessage(error, 'Could not save the agent copy.'); },
        });
    }

    finish(): void {
        if (this.copyMode) {
            this.saveCopy();
        } else {
            this.publish();
        }
    }

    cancel(): void {
        this.router.navigate(['/aiml-gateway/ai-agent-registry'], this.copyMode ? { queryParams: { tab: 'accounts' } } : {});
    }
}
